import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { DollarSignIcon, UserXIcon, TrendingDownIcon } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 28
  },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      delay: i * 0.12,
      ease: 'easeOut'
    }
  })
};
const MARKER_FONT = "'Permanent Marker', cursive";
function CountUp({
  end,
  duration = 2000,
  prefix = '',
  suffix = ''
}: {
  end: number;
  duration?: number;
  prefix?: string;
  suffix?: string;
}) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true
  });
  useEffect(() => {
    if (!inView) return;
    let start: number | null = null;
    const step = (ts: number) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [inView, end, duration]);
  return <span ref={ref} data-id="element-380">
      {prefix}
      {count.toLocaleString()}
      {suffix}
    </span>;
}
function useLiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}
// Desktop: entire arrow rotated 20° right via CSS transform
function SharpieArrowDownLeft() {
  return <svg width="100" height="90" viewBox="0 0 100 90" fill="none" style={{
    opacity: 0.95,
    transform: 'rotate(20deg)',
    transformOrigin: '84px 8px'
  }} data-id="element-381">
      <path d="M 84 8 C 84 36, 52 62, 16 78" stroke="white" strokeWidth="5" strokeLinecap="round" fill="none" data-id="element-382" />
      <path d="M 88 10 C 88 38, 56 64, 20 80" stroke="white" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.2" data-id="element-383" />
      <path d="M 32 78 L 12 82 L 15 62" stroke="white" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none" data-id="element-384" />
    </svg>;
}
// Mobile: arrow from top-left of iPhone curving down-right to notification, rotated 20° right
function SharpieArrowToNotification() {
  return <svg width="100" height="90" viewBox="0 0 100 90" fill="none" style={{
    opacity: 0.95,
    transform: 'rotate(20deg)',
    transformOrigin: '8px 6px'
  }} data-id="element-385">
      <path d="M 8 6 C 10 32, 44 56, 80 72" stroke="white" strokeWidth="5" strokeLinecap="round" fill="none" data-id="element-386" />
      <path d="M 10 11 C 12 36, 46 60, 82 76" stroke="white" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.2" data-id="element-387" />
      {/*
                      Tip at (80,72). Shaft back-dir = 222°.
                      Right wing @ 252° (+30°): tip + 25*(cos252°,sin252°) = (80-7.7, 72-23.8) = (72, 48)
                      Left  wing @ 182° (-40°): tip + 25*(cos182°,sin182°) = (80-24.9, 72-0.9) = (55, 71)
                    */}
      <path d="M 72 48 L 80 72 L 55 71" stroke="white" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none" data-id="element-388" />
    </svg>;
}
function IPhoneNotification() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    amount: 0.2
  });
  const [shown, setShown] = useState(false);
  const now = useLiveClock();
  useEffect(() => {
    if (!inView) return;
    const t = setTimeout(() => setShown(true), 700);
    return () => clearTimeout(t);
  }, [inView]);
  const timeStr = now.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).replace(/\s?(AM|PM)$/i, '');
  const dateStr = now.toLocaleDateString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
  return <div ref={ref} className="relative flex-shrink-0" style={{
    width: 260
  }} data-id="element-389">
      <div style={{
      width: 260,
      height: 560,
      borderRadius: 50,
      background: 'linear-gradient(160deg, #2a2a2a 0%, #111 40%, #1e1e1e 70%, #0a0a0a 100%)',
      padding: 3,
      position: 'relative',
      boxShadow: `inset 0 1px 0 rgba(255,255,255,0.12), inset 0 -1px 0 rgba(0,0,0,0.5), 0 0 0 1px rgba(0,0,0,0.6), 0 40px 80px rgba(0,0,0,0.7), 0 8px 20px rgba(0,0,0,0.5)`
    }} data-id="element-390">
        <div style={{
        position: 'absolute',
        left: -3,
        top: 105,
        width: 3,
        height: 30,
        borderRadius: '2px 0 0 2px',
        background: 'linear-gradient(180deg, #444, #222)'
      }} data-id="element-391" />
        <div style={{
        position: 'absolute',
        left: -3,
        top: 145,
        width: 3,
        height: 56,
        borderRadius: '2px 0 0 2px',
        background: 'linear-gradient(180deg, #444, #222)'
      }} data-id="element-392" />
        <div style={{
        position: 'absolute',
        left: -3,
        top: 210,
        width: 3,
        height: 56,
        borderRadius: '2px 0 0 2px',
        background: 'linear-gradient(180deg, #444, #222)'
      }} data-id="element-393" />
        <div style={{
        position: 'absolute',
        right: -3,
        top: 145,
        width: 3,
        height: 76,
        borderRadius: '0 2px 2px 0',
        background: 'linear-gradient(180deg, #444, #222)'
      }} data-id="element-394" />

        <div style={{
        width: '100%',
        height: '100%',
        borderRadius: 47,
        overflow: 'hidden',
        background: 'linear-gradient(180deg, #080818 0%, #0d1020 35%, #0a0d1a 100%)',
        display: 'flex',
        flexDirection: 'column'
      }} data-id="element-395">
          <div style={{
          display: 'flex',
          justifyContent: 'center',
          paddingTop: 8,
          paddingBottom: 4,
          flexShrink: 0
        }} data-id="element-396">
            <div style={{
            width: 110,
            height: 28,
            borderRadius: 16,
            background: '#000'
          }} data-id="element-397" />
          </div>

          <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0 18px 4px',
          flexShrink: 0
        }} data-id="element-398">
            <span style={{
            color: 'white',
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: '-0.2px'
          }} data-id="element-399">
              {timeStr}
            </span>
            <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4
          }} data-id="element-400">
              <svg width="14" height="10" viewBox="0 0 17 12" fill="none" data-id="element-401">
                <rect x="0" y="8" width="3" height="4" rx="0.8" fill="white" fillOpacity="0.4" data-id="element-402" />
                <rect x="4.5" y="5.5" width="3" height="6.5" rx="0.8" fill="white" fillOpacity="0.6" data-id="element-403" />
                <rect x="9" y="3" width="3" height="9" rx="0.8" fill="white" fillOpacity="0.85" data-id="element-404" />
                <rect x="13.5" y="0" width="3" height="12" rx="0.8" fill="white" data-id="element-405" />
              </svg>
              <svg width="13" height="10" viewBox="0 0 16 12" fill="none" data-id="element-406">
                <circle cx="8" cy="11" r="1.5" fill="white" data-id="element-407" />
                <path d="M4.6 7.6C5.5 6.7 6.7 6.1 8 6.1s2.5.6 3.4 1.5" stroke="white" strokeWidth="1.4" strokeLinecap="round" fill="none" data-id="element-408" />
                <path d="M1.8 4.8C3.4 3.1 5.6 2 8 2s4.6 1.1 6.2 2.8" stroke="white" strokeWidth="1.4" strokeLinecap="round" fill="none" strokeOpacity="0.55" data-id="element-409" />
              </svg>
              <svg width="21" height="10" viewBox="0 0 26 12" fill="none" data-id="element-410">
                <rect x="0.5" y="0.5" width="22" height="11" rx="3.5" stroke="white" strokeOpacity="0.38" strokeWidth="1" data-id="element-411" />
                <rect x="23" y="3.5" width="2.5" height="5" rx="1.5" fill="white" fillOpacity="0.4" data-id="element-412" />
                <rect x="2" y="2" width="16" height="8" rx="2" fill="white" data-id="element-413" />
              </svg>
            </div>
          </div>

          <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          padding: '6px 12px 0'
        }} data-id="element-414">
            <div style={{
            textAlign: 'center',
            marginBottom: 2,
            flexShrink: 0
          }} data-id="element-415">
              <p style={{
              color: 'rgba(255,255,255,0.75)',
              fontSize: 16,
              fontWeight: 400
            }} data-id="element-416">
                {dateStr}
              </p>
            </div>
            <div style={{
            textAlign: 'center',
            marginBottom: 14,
            flexShrink: 0
          }} data-id="element-417">
              <p style={{
              color: 'white',
              fontSize: 80,
              fontWeight: 300,
              letterSpacing: '-1px',
              lineHeight: 0.95
            }} data-id="element-418">
                {timeStr}
              </p>
            </div>

            <div style={{
            flexShrink: 0
          }} data-id="element-419">
              <AnimatePresence data-id="element-420">
                {shown && <motion.div initial={{
                opacity: 0,
                y: -14,
                scale: 0.93
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} transition={{
                type: 'spring',
                stiffness: 320,
                damping: 26,
                delay: 0.1
              }} data-id="element-421">
                    <div style={{
                  borderRadius: 20,
                  overflow: 'hidden',
                  background: 'rgba(28,28,48,0.82)',
                  backdropFilter: 'blur(28px)',
                  WebkitBackdropFilter: 'blur(28px)',
                  border: '1px solid rgba(255,255,255,0.14)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.45)'
                }} data-id="element-422">
                      <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '11px 14px 6px'
                  }} data-id="element-423">
                        <div style={{
                      width: 30,
                      height: 30,
                      borderRadius: 8,
                      flexShrink: 0,
                      background: 'linear-gradient(135deg, #1a7a3c, #25a855)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 15
                    }} data-id="element-424">
                          🏦
                        </div>
                        <span style={{
                      color: 'rgba(255,255,255,0.6)',
                      fontSize: 11,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      flex: 1
                    }} data-id="element-425">
                          CHASE BANK
                        </span>
                        <span style={{
                      color: 'rgba(255,255,255,0.38)',
                      fontSize: 11
                    }} data-id="element-426">
                          now
                        </span>
                      </div>
                      <div style={{
                    padding: '0 14px 13px'
                  }} data-id="element-427">
                        <p style={{
                      color: 'white',
                      fontSize: 14,
                      fontWeight: 600,
                      marginBottom: 3,
                      lineHeight: 1.3
                    }} data-id="element-428">
                          New rep sales
                        </p>
                        <p style={{
                      color: '#4ECDC4',
                      fontSize: 26,
                      fontWeight: 900,
                      lineHeight: 1,
                      marginBottom: 4
                    }} data-id="element-429">
                          +$25,173.36
                        </p>
                        <p style={{
                      color: 'rgba(255,255,255,0.48)',
                      fontSize: 12
                    }} data-id="element-430">
                          Direct deposit received
                        </p>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>

          <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '8px 38px 10px',
          flexShrink: 0
        }} data-id="element-431">
            <div style={{
            width: 46,
            height: 46,
            borderRadius: 23,
            background: 'rgba(255,255,255,0.18)',
            backdropFilter: 'blur(12px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }} data-id="element-432">
              <svg width="14" height="20" viewBox="0 0 14 22" fill="none" data-id="element-433">
                <path d="M4 2H10L12 8H2L4 2Z" fill="white" opacity="0.9" data-id="element-434" />
                <rect x="4" y="8" width="6" height="11" rx="3" fill="white" opacity="0.9" data-id="element-435" />
              </svg>
            </div>
            <div style={{
            width: 46,
            height: 46,
            borderRadius: 23,
            background: 'rgba(255,255,255,0.18)',
            backdropFilter: 'blur(12px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }} data-id="element-436">
              <svg width="22" height="18" viewBox="0 0 24 20" fill="none" data-id="element-437">
                <rect x="1" y="4" width="22" height="15" rx="3" stroke="white" strokeWidth="1.7" fill="none" opacity="0.9" data-id="element-438" />
                <circle cx="12" cy="11.5" r="4" stroke="white" strokeWidth="1.7" fill="none" opacity="0.9" data-id="element-439" />
                <path d="M8 4V3C8 2.4 8.4 2 9 2H15C15.6 2 16 2.4 16 3V4" stroke="white" strokeWidth="1.5" fill="none" opacity="0.6" data-id="element-440" />
              </svg>
            </div>
          </div>

          <div style={{
          display: 'flex',
          justifyContent: 'center',
          paddingBottom: 8,
          flexShrink: 0
        }} data-id="element-441">
            <div style={{
            width: 110,
            height: 4,
            borderRadius: 2,
            background: 'rgba(255,255,255,0.28)'
          }} data-id="element-442" />
          </div>
        </div>
      </div>
    </div>;
}
const pains = [{
  icon: DollarSignIcon,
  number: 100000,
  prefix: '$',
  suffix: '+',
  label: 'Lost per week',
  sub: 'In opportunity cost from every empty seat on your team.'
}, {
  icon: TrendingDownIcon,
  number: 12,
  prefix: '',
  suffix: ' mo',
  label: 'Behind schedule',
  sub: 'The average D2D company loses 6–12 months of growth to bad recruiting.'
}, {
  icon: UserXIcon,
  number: 3,
  prefix: '',
  suffix: ' days',
  label: 'Before they ghost',
  sub: 'The average bad hire disappears within 72 hours of starting.'
}];
function StatTile({
  pain,
  custom
}: {
  pain: (typeof pains)[0];
  custom: number;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    amount: 0.2
  });
  return <motion.div ref={ref} custom={custom} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="backdrop-blur-md rounded-2xl p-5 flex flex-col gap-3" style={{
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.1)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.08)',
    minHeight: 148
  }} data-id="element-443">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{
      backgroundColor: 'rgba(232,101,90,0.15)'
    }} data-id="element-444">
        <pain.icon className="w-4 h-4 text-[#E8655A]" data-id="element-445" />
      </div>
      <div data-id="element-446">
        <p className="text-2xl font-black text-white leading-none mb-0.5" data-id="element-447">
          <CountUp end={pain.number} prefix={pain.prefix} suffix={pain.suffix} duration={1800} data-id="element-448" />
        </p>
        <p className="text-[10px] font-bold text-[#E8655A] uppercase tracking-wider mb-1.5" data-id="element-449">
          {pain.label}
        </p>
        <p className="text-[11px] text-white/45 leading-relaxed" data-id="element-450">{pain.sub}</p>
      </div>
    </motion.div>;
}
export function ProblemSectionA() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    amount: 0.05
  });
  return <section className="relative w-full bg-[#1A2B4A] overflow-hidden py-24 px-6" data-id="element-451">
      <div className="absolute inset-0 opacity-30" style={{
      backgroundImage: 'linear-gradient(rgba(232,101,90,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(232,101,90,0.07) 1px, transparent 1px)',
      backgroundSize: '40px 40px'
    }} data-id="element-452" />
      <div className="absolute top-0 right-0 w-[500px] h-[400px] bg-[#E8655A]/6 rounded-full blur-[100px] pointer-events-none" data-id="element-453" />

      <div ref={ref} className="max-w-5xl mx-auto relative z-10" data-id="element-454">
        {/* Header — extra bottom margin so iPhone annotation doesn't crowd it */}
        <motion.div custom={0} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="text-center mb-20" data-id="element-455">
          <span className="inline-block bg-[#E8655A]/20 text-[#E8655A] text-xs font-bold uppercase px-3 py-1 rounded-full mb-4 tracking-wider border border-[#E8655A]/30" data-id="element-456">
            The Real Cost of No System
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4 leading-tight" data-id="element-457">
            Why "Post and Pray" Is{' '}
            <span className="text-[#E8655A]" data-id="element-458">Failing You</span>
          </h2>
          <p className="text-lg text-white/50 max-w-2xl mx-auto" data-id="element-459">
            These aren't estimates. This is what your competitors are watching
            you bleed — every single week you don't have a system.
          </p>
        </motion.div>

        {/* ─── DESKTOP (lg+) ─── */}
        <div className="hidden lg:block" data-id="element-460">
          <div className="relative" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 16
        }} data-id="element-461">
            <div data-id="element-462" />
            <motion.div custom={1} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="relative z-20 flex justify-center" style={{
            marginBottom: -220
          }} data-id="element-463">
              <IPhoneNotification data-id="element-464" />
            </motion.div>
            <motion.div custom={2} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="flex flex-col justify-start" style={{
            paddingTop: 80,
            paddingLeft: 24
          }} data-id="element-465">
              <p style={{
              fontFamily: MARKER_FONT,
              fontSize: '2rem',
              fontWeight: 700,
              color: 'white',
              transform: 'rotate(-3deg)',
              lineHeight: 1.25,
              textShadow: '2px 2px 0px rgba(0,0,0,0.4)'
            }} data-id="element-466">
                Lost revenue from no new recruits
              </p>
              <div style={{
              marginTop: 10
            }} data-id="element-467">
                <SharpieArrowDownLeft data-id="element-468" />
              </div>
            </motion.div>
            <div data-id="element-469" />
            <div className="relative z-30" data-id="element-470">
              <StatTile pain={pains[0]} custom={3} data-id="element-471" />
            </div>
            <div className="relative z-30" data-id="element-472" />
            <div className="relative z-30" data-id="element-473">
              <StatTile pain={pains[1]} custom={4} data-id="element-474" />
            </div>
            <div className="relative z-30" data-id="element-475">
              <StatTile pain={pains[2]} custom={5} data-id="element-476" />
            </div>
          </div>
        </div>

        {/* ─── MOBILE (< lg) ─── */}
        {/*
          Layout:
          - iPhone is centered
          - Marker text overlays the TOP-LEFT corner of the iPhone (position: absolute, top ~20px, left ~-10px)
          - Arrow starts right below/after the text and curves down-right to the notification card
          - Notification card is at ~y=190px from top of iPhone outer div
          - Text bottom is at ~top:20 + ~70px height = ~90px
          - Arrow is positioned at top:90, left:0 (starts at left side of iPhone), curves to notification at ~x=130, y=195
         */}
        <div className="lg:hidden" data-id="element-477">
          <motion.div custom={1} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="relative flex justify-center" style={{
          marginBottom: -160
        }} data-id="element-478">
            <div style={{
            position: 'relative',
            width: 260
          }} data-id="element-479">
              <IPhoneNotification data-id="element-480" />

              {/* Marker text with dark blob image centered over full two-line text */}
              <div style={{
              position: 'absolute',
              top: 8,
              left: -22,
              zIndex: 40,
              pointerEvents: 'none',
              maxWidth: 175
            }} data-id="element-481">
                {/* Subtle black radial gradient — just enough contrast, smooth fade */}
                <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%) rotate(-5deg)',
                width: '190px',
                height: '90px',
                background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.52) 30%, rgba(0,0,0,0.18) 60%, transparent 75%)',
                zIndex: 0,
                pointerEvents: 'none'
              }} data-id="element-482" />
                <p style={{
                fontFamily: MARKER_FONT,
                fontSize: '1.2rem',
                fontWeight: 400,
                color: 'white',
                transform: 'rotate(-5deg)',
                lineHeight: 1.3,
                position: 'relative',
                zIndex: 1
              }} data-id="element-483">
                  Lost revenue from no new recruits
                </p>
              </div>

              {/* Arrow: shifted left, rotated 20° right via CSS */}
              <div style={{
              position: 'absolute',
              top: 95,
              left: -18,
              zIndex: 40,
              pointerEvents: 'none'
            }} data-id="element-484">
                <SharpieArrowToNotification data-id="element-485" />
              </div>
            </div>
          </motion.div>

          {/* Tiles */}
          <div className="relative z-30 grid grid-cols-2 gap-3" data-id="element-486">
            <StatTile pain={pains[0]} custom={2} data-id="element-487" />
            <StatTile pain={pains[1]} custom={3} data-id="element-488" />
            <div className="col-span-2" data-id="element-489">
              <StatTile pain={pains[2]} custom={4} data-id="element-490" />
            </div>
          </div>
        </div>
      </div>
    </section>;
}